import { useEffect, useState } from "react";
import Layout from "@/components/layouts/Layout";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { getEvents } from "@/lib/api/events";
import { EventData } from "@/types/events";
import { useLanguage } from "@/contexts/LanguageContext";
import EventCard from "@/components/features/events/EventCard";
import Pagination from "@/components/ui/pagination";
import EventSkeleton from "@/components/loaders/EventsSkeleton";

const Events = () => {
  const { language, t } = useLanguage();
  const isRTL = language === 'ar';
  const [events, setEvents] = useState<EventData[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    itemsPerPage: 6,
    totalPages: 1,
    totalItems: 0,
  });
  const [activeTab, setActiveTab] = useState<"upcoming" | "featured" | "past">("upcoming");

  useEffect(() => {
    const loadEvents = async () => {
      setLoading(true);
      try {
        const response = await getEvents({
          language,
          page: pagination.currentPage,
          pageSize: pagination.itemsPerPage,
          filters: {
            upcoming: activeTab === "upcoming",
            featured: activeTab === "featured",
            past: activeTab === "past",
          }
        });
        
        setEvents(response.data);
        setPagination(prev => ({
          ...prev,
          totalPages: response.pagination.total_pages,
          totalItems: response.pagination.total
        }));
      } catch (err) {
        console.error("Failed to load events:", err);
        setEvents([]);
      } finally {
        setLoading(false);
      }
    };

    loadEvents();
  }, [language, pagination.currentPage, activeTab]);

  const handleTabChange = (value: string) => {
    const tab = value as "upcoming" | "featured" | "past";
    setActiveTab(tab);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handlePageChange = (page: number) => {
    setPagination(prev => ({ ...prev, currentPage: page }));
  };

  return (
    <Layout>
      <section className={`
        pt-24 pb-16 bg-gradient-to-b from-white to-gray-50 dark:from-nexus-dark dark:to-nexus-dark-light ${isRTL ? 'rtl' : 'ltr'}
      `}>
        <div className="container">
          <div className="max-w-3xl mx-auto text-center text-nexus-gray dark:text-white">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              {t("events.pageTitle")}
            </h1>
            <p className="text-xl mb-8">
              {t("events.pageDescription")}
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Button asChild className="bg-nexus-brand hover:bg-nexus-brand/90 text-white">
                <Link to="https://forms.gle/zNbyL65tkvC8BhdT6" target="_blank" rel="noopener noreferrer">
                  {t("events.hostEvent")}
                  {language === "ar" ? (
                    <ArrowRight className="mr-2 h-4 w-4 transform rotate-180" /> 
                  ) : (
                    <ArrowRight className="ml-2 h-4 w-4" /> 
                  )}
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {loading ? (
        <EventSkeleton />
      ) : (
        <section className="py-16 bg-white dark:bg-nexus-dark">
          <div className="container">
            <div className="max-w-5xl mx-auto">
              <Tabs value={activeTab} onValueChange={handleTabChange}>
                <div className={`flex justify-between items-center mb-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <TabsList className={`gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <TabsTrigger value="upcoming">{t("events.upcoming")}</TabsTrigger>
                    <TabsTrigger value="featured">{t("events.featured")}</TabsTrigger>
                    <TabsTrigger value="past">{t("events.past")}</TabsTrigger>
                  </TabsList>

                  <div className={`text-sm text-gray-500 dark:text-gray-400 ${isRTL ? 'text-right' : 'text-left'}`}>  
                    {t("events.showing")} {events.length} {t("events.of")} {pagination.totalItems}
                  </div>
                </div>

                <TabsContent value="upcoming" className="space-y-6">
                  {events.length === 0 ? (
                    <div className="text-center text-gray-500 dark:text-gray-400">
                      {t("events.noUpcoming")}
                    </div>
                  ) : (
                    <>
                      <div className="grid gap-6 sm:grid-cols-1 lg:grid-cols-1">
                        {events.map((event) => (
                          <EventCard key={event.id} event={event} />
                        ))}
                      </div>
                      {pagination.totalPages > 1 && (
                        <Pagination
                          itemsPerPage={pagination.itemsPerPage}
                          currentPage={pagination.currentPage}
                          totalPages={pagination.totalPages}
                          onPageChange={handlePageChange}
                        />
                      )}
                    </>
                  )}
                </TabsContent>

                <TabsContent value="featured" className="space-y-6">
                  {events.length === 0 ? (
                    <div className="text-center text-gray-500 dark:text-gray-400">
                      {t("events.noFeatured")}
                    </div>
                  ) : (
                    <>
                      <div className="grid gap-6 sm:grid-cols-1 lg:grid-cols-1">
                        {events.map((event) => (
                          <EventCard key={event.id} event={event} />
                        ))}
                      </div>
                      {pagination.totalPages > 1 && (
                        <Pagination
                          itemsPerPage={pagination.itemsPerPage}
                          currentPage={pagination.currentPage}
                          totalPages={pagination.totalPages}
                          onPageChange={handlePageChange}
                        />
                      )}
                    </>
                  )}
                </TabsContent>

                <TabsContent value="past" className="space-y-6">
                  {events.length === 0 ? (
                    <div className="text-center text-gray-500 dark:text-gray-400">
                      {t("events.noPast")}
                    </div>
                  ) : (
                    <>
                      <div className="grid gap-6 sm:grid-cols-1 lg:grid-cols-1">
                        {events.map((event) => (
                          <EventCard key={event.id} event={event} />
                        ))}
                      </div>
                      {pagination.totalPages > 1 && (
                        <Pagination
                          itemsPerPage={pagination.itemsPerPage}
                          currentPage={pagination.currentPage}
                          totalPages={pagination.totalPages}
                          onPageChange={handlePageChange}
                        />
                      )}
                    </>
                  )}
                </TabsContent>
              </Tabs>
            </div>
          </div>
        </section>
      )}
    </Layout>
  );
};

export default Events;
